import { inject, Injectable } from '@angular/core';
import { getAssociatedTokenAddressSync, TOKEN_PROGRAM_ID } from '@solana/spl-token';
import { TransactionMessage, VersionedTransaction } from '@solana/web3.js';

import { environment } from '../../../environments/environment';
import { SwapStatus } from '../../shared/constants/app-enums';
import { AnalyticsService } from '../analytics.service';
import { PresaleDataService } from '../connection-services/data.service';
import { ReownService } from '../connection-services/reown.service';
import { EvmPurchseService } from '../evm-services/evm-purchase.service';
import { SolanaInitialisationService } from './solana-initialise.service';
import { PixelService } from '../pixel.service';

@Injectable({
    providedIn: 'root',
})
export class SolPurchaseService {
    private readonly evmPurchaseService = inject(EvmPurchseService);
    private readonly solService = inject(SolanaInitialisationService);
    private readonly analyticsService = inject(AnalyticsService);
    private readonly dataService = inject(PresaleDataService);
    private readonly pixelService = inject(PixelService);
    private readonly reownService = inject(ReownService);

    async getRequiredInfoAddress() {
        const accountInfo: any = await this.solService.program.account.presaleAccount.fetch(this.solService.presaleAccountAddress[0]); // this.presaleAccountAddress[0] = Public Key for Presale Account Address
        const userInfoAddress = this.solService.web3PublicKey.findProgramAddressSync([this.solService.solWalletPubkey.toBuffer()], this.solService.programId);

        return { userInfoAddress, accountInfo };
    }

    /**
     * this function is swaping the native token with app token
     * @param amount: app token amount
     * @param value: eth token value
     */

    async swapNativeTokens(tokenNo: number, value: number, isBuyStake: boolean = false, isRetry: boolean = false) {
        this.evmPurchaseService.setTransactionStatus(SwapStatus.in_progess);
        this.vwoData('walletTransactionStatus', { walletTransactionPrompted: true, walletTransactionStatus: '', walletTransactionWalletAddress: this.dataService.walletAddressSOL, walletTransactionHash: '' });

        const { userInfoAddress, accountInfo } = await this.getRequiredInfoAddress();

        const transactionObj = await this.solService.program.transaction.buyToken(new this.solService.BN(tokenNo), {
            accounts: {
                presaleAccount: this.solService.presaleAccountAddress[0],
                stateInfo: this.solService.stateInfoAddress[0],
                userInfo: userInfoAddress[0],
                signer: this.solService.solWalletPubkey, //this.provider.wallet.publicKey,
                // pythPriceFeed: new this.solanaWeb3.this.web3PublicKey(environment.pythPriceFeed),
                paymentWallet: accountInfo.paymentWallet,
                systemProgram: this.solService.systemProgram.programId,
                otherAccount: new this.solService.web3PublicKey(environment.sol.otherAccountSol),
                chainlinkFeed: new this.solService.web3PublicKey(environment.sol.chainlinkFeed),
                chainlinkProgram: new this.solService.web3PublicKey(environment.sol.chainlinkProgram),
            },
        });
        transactionObj.feePayer = this.solService.solWalletPubkey;
        const { blockhash, lastValidBlockHeight } = await this.solService.connection?.getLatestBlockhash();
        transactionObj.recentBlockhash = blockhash;

        // Generate unique transaction ID for this buy flow
        const txId = this.pixelService.generateTransactionId();

        this.pixelService.sendPixelEvent('buy_event', {
            action: 'initiated',
            tx_id: txId,
            wallet_address: this.dataService.walletAddressSOL,
            wallet_type: 'SOL',
            wallet_name: this.reownService.connectedWalletName || this.dataService.connectedWalletName,
            token_symbol: environment.tokenName,
            chain: 'SOL',
            chain_id: environment.sol.chainIdInt,
            amount_token_expected: tokenNo,
            amount_usd_estimated: +(tokenNo * this.dataService.tokenomics[this.dataService.balanceDataSOl.currentStep].tokenUSDT).toFixed(2),
            payment_method: 'SOL',
            type: isBuyStake ? 'buy_and_stake' : 'buy'
        });

        try {
            const sig = await this._signAndSendWithFallback(transactionObj, { blockhash, lastValidBlockHeight }, isRetry);

            if (sig) {
                this.evmPurchaseService.setTransactionStatus(SwapStatus.complete, sig);
                this.vwoData('walletTransactionStatus', { walletTransactionPrompted: false, walletTransactionStatus: 'approved', walletTransactionWalletAddress: this.dataService.walletAddress, walletTransactionHash: sig });
                this.vwoData('successfulTransaction', {
                    walletAddress: this.dataService.walletAddress,
                    transactionId: sig,
                    mode: 'CARYPTO',
                    walletName: this.dataService.connectedWalletName,
                    purchaseType: "buy",
                    tokenAmountPurchase: tokenNo,
                    amountSpent: value,
                    cryptoType: "sol",
                    usdAmount: +(+(tokenNo * this.dataService.tokenomics[this.dataService.balanceDataEVM.currentStep].tokenUSDT).toFixed(2)),
                });
                // Track success (transaction confirmed)
                try {
                    const txDetails = await this.solService.connection.getTransaction(sig, { maxSupportedTransactionVersion: 0 });
                    const blockNumber = txDetails?.slot;
                    const blockTimestamp = txDetails?.blockTime ? new Date(txDetails.blockTime * 1000).toISOString() : new Date().toISOString();

                    this.pixelService.sendPixelEvent('buy_event', {
                        action: 'success',
                        tx_id: txId,
                        wallet_address: this.dataService.walletAddressSOL,
                        wallet_type: 'SOL',
                        wallet_name: this.reownService.connectedWalletName || this.dataService.connectedWalletName,
                        token_symbol: environment.tokenName,
                        chain: 'SOL',
                        chain_id: environment.sol.chainIdInt,
                        amount_token_expected: tokenNo,
                        amount_usd_estimated: value, // Use value directly as it was passed
                        payment_method: 'SOL',
                        tx_hash: sig,
                        block_number: blockNumber,
                        block_timestamp: blockTimestamp,
                        type: isBuyStake ? 'buy_and_stake' : 'buy'
                    });
                } catch {
                    this.pixelService.sendPixelEvent('buy_event', {
                        action: 'success',
                        tx_id: txId,
                        wallet_address: this.dataService.walletAddressSOL,
                        wallet_type: 'SOL',
                        wallet_name: this.reownService.connectedWalletName || this.dataService.connectedWalletName,
                        token_symbol: environment.tokenName,
                        chain: 'SOL',
                        chain_id: environment.sol.chainIdInt,
                        amount_token_expected: tokenNo,
                        amount_usd_estimated: value,
                        payment_method: 'SOL',
                        tx_hash: sig,
                        type: isBuyStake ? 'buy_and_stake' : 'buy'
                    });
                }

            } else {
                this.evmPurchaseService.setTransactionStatus(SwapStatus.failed, sig);
                this.vwoData('walletTransactionStatus', { walletTransactionPrompted: false, walletTransactionStatus: 'failed', walletTransactionWalletAddress: this.dataService.walletAddress, walletTransactionHash: sig });
                this.vwoData('failedAndRejectTransaction', {
                    transactionStatus: 'failed',
                    fundSpentAmount: value,
                    tokenAmountPurchase: tokenNo,
                    purchaseType: isBuyStake ? 'buy_and_stake' : 'buy',
                    tokenPurchaseMethod: 'sol',
                    transactionId: sig,
                    walletAddress: this.dataService.walletAddress,
                    walletType: this.dataService.connectedWalletName,
                });
                // Track buy failed
                this.pixelService.sendPixelEvent('buy_event', {
                    action: 'failed',
                    tx_id: txId,
                    wallet_address: this.dataService.walletAddressSOL,
                    wallet_type: 'SOL',
                    wallet_name: this.reownService.connectedWalletName || this.dataService.connectedWalletName,
                    token_symbol: environment.tokenName,
                    chain: 'SOL',
                    chain_id: environment.sol.chainIdInt,
                    amount_token_expected: tokenNo,
                    amount_usd_estimated: +(tokenNo * this.dataService.tokenomics[this.dataService.balanceDataSOl.currentStep].tokenUSDT).toFixed(2),
                    payment_method: 'SOL',
                    failure_type: 'transaction_failed',
                    failure_message: 'Transaction failed',
                    type: isBuyStake ? 'buy_and_stake' : 'buy'
                });
            }
        } catch (error) {
            this.evmPurchaseService.setTransactionStatus(SwapStatus.failed);
            this.vwoData('walletTransactionStatus', { walletTransactionPrompted: false, walletTransactionStatus: 'failed', walletTransactionWalletAddress: this.dataService.walletAddress, walletTransactionHash: '' });
            this.vwoData('failedAndRejectTransaction', {
                transactionStatus: 'failed',
                fundSpentAmount: value,
                tokenAmountPurchase: tokenNo,
                purchaseType: isBuyStake ? 'buy_and_stake' : 'buy',
                tokenPurchaseMethod: 'sol',
                transactionId: '',
                walletAddress: this.dataService.walletAddress,
                walletType: this.dataService.connectedWalletName,
            });
            this.pixelService.sendPixelEvent('buy_event', {
                action: 'failed',
                tx_id: txId,
                wallet_address: this.dataService.walletAddressSOL,
                wallet_type: 'SOL',
                wallet_name: this.reownService.connectedWalletName || this.dataService.connectedWalletName,
                token_symbol: environment.tokenName,
                chain: 'SOL',
                chain_id: environment.sol.chainIdInt,
                amount_token_expected: tokenNo,
                amount_usd_estimated: +(tokenNo * this.dataService.tokenomics[this.dataService.balanceDataSOl.currentStep].tokenUSDT).toFixed(2),
                payment_method: 'SOL',
                failure_type: 'rpc_error',
                failure_message: (error as any)?.message || 'Transaction failed',
                type: isBuyStake ? 'buy_and_stake' : 'buy'
            });
            if (!isRetry) this.swapNativeTokens(tokenNo, value, isBuyStake, true);
        }
    }

    /**
  * this function is checking allowence
  * amount before swaping the usdt token with app token
  * @param amount: app token amount
  * @param value: usdt token value
  */

    async buyWithStableToken(tokenNo: number, value: number, isUsdc: boolean, isBuyStake: boolean = false, isRetry = false) {
        this.evmPurchaseService.setTransactionStatus(SwapStatus.in_progess);
        this.vwoData('walletTransactionStatus', { walletTransactionPrompted: true, walletTransactionStatus: '', walletTransactionWalletAddress: this.dataService.walletAddressSOL, walletTransactionHash: '' });
        // Generate unique transaction ID
        const txId = this.pixelService.generateTransactionId();
        const paymentMethod = isUsdc ? 'usdc' : 'usdt';

        // Track buy initiated
        this.pixelService.sendPixelEvent('buy_event', {
            action: 'initiated',
            tx_id: txId,
            wallet_address: this.dataService.walletAddressSOL,
            wallet_type: 'SOL',
            wallet_name: this.reownService.connectedWalletName || this.dataService.connectedWalletName,
            token_symbol: environment.tokenName,
            chain: 'SOL',
            chain_id: environment.sol.chainIdInt,
            amount_token_expected: tokenNo,
            amount_usd_estimated: value,
            payment_method: paymentMethod,
            type: isBuyStake ? 'buy_and_stake' : 'buy'
        });

        let transactionObj;
        const { userInfoAddress, accountInfo } = await this.getRequiredInfoAddress();
        if (isUsdc) {
            const usdcTokenAccount = await getAssociatedTokenAddressSync(this.solService.USDC_TOKEN_ADDRESS, this.solService.solWalletPubkey, true, TOKEN_PROGRAM_ID, this.solService.associate_programId);
            transactionObj = await this.solService.program.transaction.buyWithUsdc(new this.solService.BN(tokenNo), {
                accounts: {
                    presaleAccount: this.solService.presaleAccountAddress[0],
                    stateInfo: this.solService.stateInfoAddress[0],
                    userInfo: userInfoAddress[0],
                    signer: this.solService.solWalletPubkey,
                    paymentWalletUsdcAccount: accountInfo.paymentWalletUsdcAccount,
                    userUsdcAccount: usdcTokenAccount,
                    tokenProgram: new this.solService.web3PublicKey('TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA'),
                    systemProgram: this.solService.systemProgram.programId,
                    otherAccount: new this.solService.web3PublicKey(environment.sol.otherAccountUsdc),
                },
            });
        } else {
            const usdtTokenAccount = await getAssociatedTokenAddressSync(this.solService.USDT_TOKEN_ADDRESS, this.solService.solWalletPubkey, true, TOKEN_PROGRAM_ID, this.solService.associate_programId);
            transactionObj = await this.solService.program.transaction.buyWithUsdt(new this.solService.BN(tokenNo), {
                accounts: {
                    presaleAccount: this.solService.presaleAccountAddress[0],
                    stateInfo: this.solService.stateInfoAddress[0],
                    userInfo: userInfoAddress[0],
                    signer: this.solService.solWalletPubkey,
                    paymentWalletUsdtAccount: accountInfo.paymentWalletUsdtAccount,
                    userUsdtAccount: usdtTokenAccount,
                    tokenProgram: new this.solService.web3PublicKey('TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA'),
                    systemProgram: this.solService.systemProgram.programId,
                    otherAccount: new this.solService.web3PublicKey(environment.sol.otherAccountUsdt),
                },
            });
        }
        transactionObj.feePayer = this.solService.solWalletPubkey;
        const { blockhash, lastValidBlockHeight } = await this.solService.connection?.getLatestBlockhash();
        transactionObj.recentBlockhash = blockhash
        try {
            // ---- SIGN & SEND (with fallback) ----
            const sig = await this._signAndSendWithFallback(transactionObj, { blockhash, lastValidBlockHeight }, isRetry);
            this.evmPurchaseService.setTransactionStatus(SwapStatus.complete, sig);
            this.vwoData('walletTransactionStatus', { walletTransactionPrompted: false, walletTransactionStatus: 'approved', walletTransactionWalletAddress: this.dataService.walletAddress, walletTransactionHash: sig });
            this.vwoData('successfulTransaction', {
                walletAddress: this.dataService.walletAddress,
                transactionId: sig,
                mode: 'CARYPTO',
                walletName: this.dataService.connectedWalletName,
                purchaseType: "buy",
                tokenAmountPurchase: tokenNo,
                amountSpent: value,
                cryptoType: isUsdc ? "sol_usdc" : "sol_usdt",
                usdAmount: +value
            });
            // Track success
            try {
                const txDetails = await this.solService.connection.getTransaction(sig, { maxSupportedTransactionVersion: 0 });
                const blockNumber = txDetails?.slot;
                const blockTimestamp = txDetails?.blockTime ? new Date(txDetails.blockTime * 1000).toISOString() : new Date().toISOString();

                this.pixelService.sendPixelEvent('buy_event', {
                    action: 'success',
                    tx_id: txId,
                    wallet_address: this.dataService.walletAddressSOL,
                    wallet_type: 'SOL',
                    wallet_name: this.reownService.connectedWalletName || this.dataService.connectedWalletName,
                    token_symbol: environment.tokenName,
                    chain: 'SOL',
                    chain_id: environment.sol.chainIdInt,
                    amount_token_expected: tokenNo,
                    amount_usd_estimated: value,
                    payment_method: isUsdc ? 'usdc' : 'usdt',
                    tx_hash: sig,
                    block_number: blockNumber,
                    block_timestamp: blockTimestamp,
                    type: isBuyStake ? 'buy_and_stake' : 'buy'
                });
            } catch {
                this.pixelService.sendPixelEvent('buy_event', {
                    action: 'success',
                    tx_id: txId,
                    wallet_address: this.dataService.walletAddressSOL,
                    wallet_type: 'SOL',
                    wallet_name: this.reownService.connectedWalletName || this.dataService.connectedWalletName,
                    token_symbol: environment.tokenName,
                    chain: 'SOL',
                    chain_id: environment.sol.chainIdInt,
                    amount_token_expected: tokenNo,
                    amount_usd_estimated: value,
                    payment_method: isUsdc ? 'usdc' : 'usdt',
                    tx_hash: sig,
                    type: isBuyStake ? 'buy_and_stake' : 'buy'
                });
            }
            // this.analyticsService.windowDataLayer('swap', 'swapSuccessful', 3, 1, 0, isUsdc ? 'USDC' : 'USDT', environment.tokenName, value, tokenNo, this.solWalletAddress, this.swapHash, this.balanceData, environment.sol.chainIdInt, this.evmService.tokenomics[this.balanceData.currentStep].tokenUSDT);
        } catch (error) {
            this.evmPurchaseService.setTransactionStatus(SwapStatus.failed);
            this.vwoData('walletTransactionStatus', { walletTransactionPrompted: false, walletTransactionStatus: 'failed', walletTransactionWalletAddress: this.dataService.walletAddress, walletTransactionHash: '' });
            this.vwoData('failedAndRejectTransaction', {
                transactionStatus: 'failed',
                fundSpentAmount: value,
                tokenAmountPurchase: tokenNo,
                purchaseType: 'buy',
                tokenPurchaseMethod: 'sol',
                transactionId: '',
                walletAddress: this.dataService.walletAddress,
                walletType: this.dataService.connectedWalletName,
            });
            this.pixelService.sendPixelEvent('buy_event', {
                action: 'failed',
                tx_id: txId,
                wallet_address: this.dataService.walletAddressSOL,
                wallet_type: 'SOL',
                wallet_name: this.reownService.connectedWalletName || this.dataService.connectedWalletName,
                token_symbol: environment.tokenName,
                chain: 'SOL',
                chain_id: environment.sol.chainIdInt,
                amount_token_expected: tokenNo,
                amount_usd_estimated: +(tokenNo * this.dataService.tokenomics[this.dataService.balanceDataSOl.currentStep].tokenUSDT).toFixed(2),
                payment_method: paymentMethod,
                failure_type: 'rpc_error',
                failure_message: (error as any)?.message || 'Transaction failed',
                type: isBuyStake ? 'buy_and_stake' : 'buy'
            });
            if (!isRetry) this.buyWithStableToken(tokenNo, value, isUsdc, isBuyStake, true);
        }
    }

    /**
     * Try wallet.signAndSendTransaction if available.
     * Otherwise: signTransaction -> sendRawTransaction -> confirmTransaction.
     */
    private async _signAndSendWithFallback(tx: any, bh: { blockhash: string; lastValidBlockHeight: number }, isRetry = false): Promise<string> {
        const wallet: any = this.solService.provider?.wallet;
        if (!wallet) throw new Error('Wallet not initialized');
        this.evmPurchaseService.setTransactionStatus(SwapStatus.in_progess);

        const messageV0 = new TransactionMessage({
            payerKey: this.solService.solWalletPubkey,
            recentBlockhash: bh.blockhash,
            instructions: tx.instructions,
        }).compileToV0Message();

        const versionedTx = new VersionedTransaction(messageV0);
        // 1) Wallet one-shot (many WC wallets DON'T support this)
        if (typeof wallet.signAndSendTransaction === 'function' && !isRetry) {
            const res = await wallet.signAndSendTransaction(versionedTx);
            const signature: string = res?.signature ?? res; // some wallets return string directly
            if (!signature) throw new Error('No signature returned from signAndSendTransaction');

            await this.solService.connection?.confirmTransaction({ signature, ...bh }, 'confirmed');
            return signature;
        }

        // 2) Fallback: sign, then send
        if (typeof wallet.signTransaction !== 'function') {
            throw new Error('Wallet does not support signTransaction');
        }

        const signed = await wallet.signTransaction(versionedTx);
        const raw = signed.serialize();

        const signature = await this.solService.connection?.sendRawTransaction(raw, {
            skipPreflight: false,
            maxRetries: 5,
            preflightCommitment: 'confirmed',
        });

        await this.solService.connection?.confirmTransaction({ signature, ...bh }, 'confirmed');
        return signature;
    }

    vwoData(eventName: string, eventParams: any, hasParam = true) {
        this.analyticsService.vwoData(eventName, eventParams, hasParam);
    }

    async buyWithWert(tokenNo: number, value: number) {
        this.evmPurchaseService.setTransactionStatus(SwapStatus.in_progess);

        try {
            const { userInfoAddress, accountInfo } = await this.getRequiredInfoAddress();
            const ix = await this.solService.program.methods
                .buyTokenWert(new this.solService.BN(tokenNo), new this.solService.BN(+(value * this.solService.lamports_per_sol).toFixed(0)))
                .accounts({
                    presaleAccount: this.solService.presaleAccountAddress[0],
                    stateInfo: this.solService.stateInfoAddress[0],
                    userInfo: userInfoAddress[0],
                    signer: new this.solService.web3PublicKey(environment.wertSigner),
                    user: this.solService.solWalletPubkey,
                    paymentWallet: accountInfo.paymentWallet,
                    systemProgram: this.solService.systemProgram.programId,
                    otherAccount: new this.solService.web3PublicKey(environment.sol.otherAccountSol),
                    chainlinkFeed: new this.solService.web3PublicKey(environment.sol.chainlinkFeed),
                    chainlinkProgram: new this.solService.web3PublicKey(environment.sol.chainlinkProgram),
                })
                .instruction();

            // const response = await this.program.methods
            //   .buyTokenWert(new this.solService.BN(tokenNo), new this.solService.BN(+(value * this.lamports_per_sol).toFixed(0)))
            //   .accounts({
            // presaleAccount: this.solService.presaleAccountAddress[0],
            // stateInfo: this.solService.stateInfoAddress[0],
            // userInfo: userInfoAddress[0],
            // signer: new this.solService.web3PublicKey(environment.wertSigner),
            // user: this.solService.solWalletPubkey,
            // paymentWallet: accountInfo.paymentWallet,
            // systemProgram: this.solService.systemProgram.programId,
            // otherAccount: new this.solService.web3PublicKey(environment.sol.otherAccountSol),
            // chainlinkFeed: new this.solService.web3PublicKey(environment.sol.chainlinkFeed),
            // chainlinkProgram: new this.solService.web3PublicKey(environment.sol.chainlinkProgram),
            // })
            // .transaction();

            // response.feePayer = new this.web3PublicKey(environment.wertSigner);

            // const recentBlockHash = await (
            //   await this.connection.getLatestBlockhash()
            // ).blockhash;


            const wertPayload = {
                program_id: ix.programId,
                accounts: ix.keys.map((k: any) => ({
                    address: k.pubkey,
                    is_signer: k.isSigner,
                    is_writable: k.isWritable,
                })),
                data: Buffer.from(ix.data).toString('hex'),
            };
            return Buffer.from(JSON.stringify(wertPayload)).toString('hex');
        } catch {
            this.evmPurchaseService.setTransactionStatus(SwapStatus.failed);
            return '';
        }
    }
}
